import { Injectable } from '@angular/core';

import { OfferMapping } from '@cds/query-objects';
import { PrettyUrlsConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { StringDictionary } from '@frontend/sports/common/core/utils/extended-types';
import {
    FavouriteChangeType,
    FavouriteType,
    FavouritesService,
    FavouritesSport,
    FavouritesViewModel,
    isFixtureFavourite,
} from '@frontend/sports/favourites/core/feature';
import { SportsProductUtils } from '@frontend/sports/host-app/sports-product/feature/utils';
import { FavouritesDashboardUrlParam } from '@frontend/sports/navigation/core/feature/url-helper';
import { cloneDeep, forIn, groupBy, sortBy, sum, sumBy, uniqBy } from 'lodash-es';
import { BehaviorSubject, Observable, Subject, map, shareReplay } from 'rxjs';

import { LiveFavouriteFixturesService } from '../favourites/live-favourites-fixtures.service';
import {
    FavouritedItems,
    FavouritesBaseFilter,
    FavouritesEditFilterModel,
    FavouritesEditSection,
    FavouritesEditViewModel,
    FavouritesFilterModel,
    FavouritesSectionModel,
    FavouritesTabFilter,
    FiltersForRoute,
} from './favourites-dashboard.model';
import { FavouritesSectionTabState, FavouritesState, FavouritesStateService } from './favourites-state.service';
import { TranslatorService } from './translator.service';

@Injectable({ providedIn: 'root' })
export class FavouritesFilterService {
    private favourites: FavouritesViewModel[] = [];
    private filter: FavouritesFilterModel;
    private _filterChanged$ = new Subject<FavouritesFilterModel>();
    private saveChangeSubject = new BehaviorSubject<string>('');
    private closeModalSubject = new BehaviorSubject<string>('');

    readonly change$ = this._filterChanged$.pipe(shareReplay());

    constructor(
        private favouritesService: FavouritesService,
        private siteCore: Sitecore,
        private urlConfig: PrettyUrlsConfig,
        private stateService: FavouritesStateService,
        private liveFavouriteFixturesService: LiveFavouriteFixturesService,
        private translatorService: TranslatorService,
        productUtils: SportsProductUtils,
    ) {
        this.favouritesService.change.pipe(productUtils.filterSportsEmitLast()).subscribe((changedFavourites) => {
            this.liveFavouriteFixturesService.refresh(OfferMapping.None);
            this.favourites = changedFavourites.favourites;
            if (changedFavourites.changeType === FavouriteChangeType.Save) {
                this.saveChangeSubject.next('');
            }
            this.favouritesChanged();
        });
    }

    getFilters(): FavouritesFilterModel {
        return this.filter;
    }

    getCurrentFavouritesList(): FavouritesViewModel[] {
        return cloneDeep(this.favourites);
    }

    saveCurrentFavouritesList(favs: FavouritesViewModel[]): Observable<void> {
        return this.favouritesService.save(favs);
    }

    getEditFilters(): FavouritesEditFilterModel {
        const newFilter: FavouritesEditFilterModel = this.getEmptyEditFilter();

        newFilter.upcomming = this.fillEditModeUpcommingSection();
        newFilter.fixtures = this.fillEditModeFixturesSection(this.filter.fixtures);
        newFilter.competitions = this.fillEditModeSection(this.filter.competitions);
        newFilter.participants = this.fillEditModeSection(this.filter.participants);

        return newFilter;
    }

    private fillEditModeUpcommingSection(): FavouritesEditSection[] {
        const result: FavouritesEditSection[] = [];
        for (const s of this.filter.upcomming.gridSections) {
            const fav = this.favourites.find((f) => f.sport && f.sport.id.toString() === s.id && !!f.upcomingOrder);
            result.push({
                order: fav ? fav.upcomingOrder! : 0,
                id: s.id,
                name: s.title,
                icon: 'sports-icon sports-' + s.id,
                count: s.count!,
            });
        }

        return sortBy(result, (s) => s.order);
    }

    private fillEditModeFixturesSection(tab: FavouritesTabFilter): FavouritesEditSection[] {
        const value: FavouritesEditSection[] = [];

        for (const section of tab.gridSections) {
            const fixturesPerSport = this.favourites.filter((f) => f.sport.id.toString() === section.id && isFixtureFavourite(f));
            if (!fixturesPerSport) {
                continue;
            }
            const favs: FavouritesEditViewModel[] = [];
            for (const fav of fixturesPerSport) {
                favs.push({ ...fav, deleted: false });
            }
            const currOrder = favs.find((f) => !!f.order);
            value.push({
                order: currOrder ? currOrder.order! : 0,
                id: section.id,
                name: section.title,
                favs: sortBy(favs, (f) => f.order || 0),
                icon: 'sports-icon sports-' + section.id,
                expanded: true,
                count: section.count!,
            });
        }

        return sortBy(value, (section) => section.order);
    }

    private fillEditModeSection(tab: FavouritesTabFilter): FavouritesEditSection[] {
        const value: FavouritesEditSection[] = [];

        for (const section of tab.gridSections) {
            const favs: FavouritesEditViewModel[] = [];
            let sectionOrder = 0;
            for (const pill of section.gridHeaderPills!) {
                const fav = this.favourites.find((f) => f.itemId === pill.id && f.type === pill.type);
                if (!fav) {
                    continue;
                }
                if (!sectionOrder && !!fav.order) {
                    sectionOrder = fav.order;
                }

                const icon = pill.icon;
                favs.push({ ...fav, deleted: false, order: fav.order || 0, icon });
            }
            value.push({
                order: sectionOrder,
                id: section.id,
                name: section.title,
                favs: sortBy(favs, (f) => f.order || 0),
                icon: 'sports-icon sports-' + section.id,
                expanded: true,
                count: section.count!,
            });
        }

        return sortBy(value, (s) => s.order);
    }

    private getEmptyEditFilter(): FavouritesEditFilterModel {
        return {
            upcomming: [],
            competitions: [],
            participants: [],
            fixtures: [],
        };
    }

    getList(): Observable<FavouritesFilterModel> {
        return this.favouritesService.getList(true).pipe(map((favs) => this.buildFilter(favs)));
    }

    get saveChange(): Observable<string> {
        return this.saveChangeSubject;
    }

    get closeEdit(): Observable<string> {
        return this.closeModalSubject;
    }

    closeEditDialog(code: string = 'close'): void {
        this.closeModalSubject.next(code);
    }

    getFiltersForRoute(tab: string, favourites: FavouritesFilterModel): FiltersForRoute {
        let favouritesTabFilter: FavouritesTabFilter;
        let favouritesSectionModel: FavouritesSectionModel[] = [];
        let currentRoute: keyof FavouritesState = '' as keyof FavouritesState;

        switch (tab) {
            case FavouritesDashboardUrlParam.Upcoming:
            case this.urlConfig.translations[FavouritesDashboardUrlParam.Upcoming]:
                favouritesTabFilter = this.getFavouritesTabFilter(favourites.upcomming);
                favouritesSectionModel = favourites.upcomming.generalPills;
                currentRoute = FavouritesDashboardUrlParam.Upcoming;
                break;
            case FavouritesDashboardUrlParam.Competitions:
            case this.urlConfig.translations[FavouritesDashboardUrlParam.Competitions]:
                favouritesTabFilter = this.getFavouritesTabFilter(favourites.competitions);
                favouritesSectionModel = favourites.competitions.gridSections;
                currentRoute = FavouritesDashboardUrlParam.Competitions;
                break;
            case this.urlConfig.translations[FavouritesDashboardUrlParam.Players]:
            case FavouritesDashboardUrlParam.Players:
                favouritesTabFilter = this.getFavouritesTabFilter(favourites.participants);
                favouritesSectionModel = favourites.participants.gridSections;
                currentRoute = FavouritesDashboardUrlParam.Players;
                break;
            case FavouritesDashboardUrlParam.Events:
            case this.urlConfig.translations[FavouritesDashboardUrlParam.Events]:
                favouritesTabFilter = this.getFavouritesTabFilter(favourites.fixtures);
                favouritesSectionModel = favourites.fixtures.generalPills;
                currentRoute = FavouritesDashboardUrlParam.Events;
                break;
            default:
                favouritesTabFilter = this.getFavouritesTabFilter();
                break;
        }

        return { tabFilter: favouritesTabFilter, section: favouritesSectionModel, currentRoute };
    }

    private favouritesChanged(): void {
        if (!this.favourites?.length) {
            this.clearFilter();
            this._filterChanged$.next(this.filter);

            return;
        }

        this.filter = this.buildFilter(this.favourites);
        this._filterChanged$.next(this.filter);
    }

    private buildFilter(favourites: FavouritesViewModel[]): FavouritesFilterModel {
        const newFilter: FavouritesFilterModel = this.getEmptyFilter();

        const favs = favourites.filter((fav) => !isFixtureFavourite(fav) || fav.count !== 0);

        const participants = favourites.filter((f) => f.type === FavouriteType.Participant || f.type === FavouriteType.ParticipantV2);
        const fixtures = favourites.filter((f) => isFixtureFavourite(f) && f.count !== 0);
        const competitions = favourites.filter((f) => f.type === FavouriteType.League);
        newFilter.upcomming = {
            generalPills: this.getGeneralPills(FavouritesDashboardUrlParam.Upcoming, favs, true, true),
            gridSections: this.getGeneralPills(FavouritesDashboardUrlParam.Upcoming, favs, false, true),
        };
        newFilter.fixtures = {
            generalPills: this.getGeneralPills(FavouritesDashboardUrlParam.Events, fixtures, true),
            gridSections: this.getGeneralPills(FavouritesDashboardUrlParam.Events, fixtures, false),
        };
        newFilter.competitions = {
            generalPills: [],
            gridSections: this.getGridSections(FavouritesDashboardUrlParam.Competitions, competitions),
        };
        newFilter.participants = {
            generalPills: [],
            gridSections: this.getGridSections(FavouritesDashboardUrlParam.Players, participants),
        };

        return newFilter;
    }

    private clearFilter(): void {
        this.filter = this.getEmptyFilter();
    }

    private getEmptyFilter(): FavouritesFilterModel {
        return {
            upcomming: {
                generalPills: [],
                gridSections: [],
            },
            competitions: {
                generalPills: [],
                gridSections: [],
            },
            participants: {
                generalPills: [],
                gridSections: [],
            },
            fixtures: {
                generalPills: [],
                gridSections: [],
            },
        };
    }

    private getGridSections(routeTab: keyof FavouritesSectionTabState, favs: FavouritesViewModel[]): FavouritesSectionModel[] {
        if (!favs?.length) {
            return [];
        }
        const tmpFavs = [...favs];
        const value = <FavouritesSectionModel[]>[];

        let currentAll: FavouritesBaseFilter;
        let currBase: FavouritesBaseFilter[];

        const groups = sortBy(
            groupBy(tmpFavs, (f) => f.sport.id),
            (g) => g[0].order || tmpFavs.indexOf(g[0]),
        );

        for (const group in groups) {
            currBase = sortBy(
                groups[group].map((f) => ({
                    id: f.itemId,
                    title: f.name,
                    icon: f.type === FavouriteType.League ? 'country-icon c' + (f.region?.id || 6) : '',
                    count: f.count,
                    type: f.type,
                    active: false,
                    order: f.order,
                    classes: this.getClasses(f.count),
                    imageProfile: f.imageProfile,
                    sportId: f.sport.id,
                })),
                (el) => el.order,
            );

            const currSportCount = sum(currBase.map((item) => item.count));
            if (currBase.length > 1) {
                const allItems: FavouritedItems[] = [];
                for (const item in FavouriteType) {
                    const currFavs = currBase.filter((f) => f.type && f.type === FavouriteType[item as keyof typeof FavouriteType]);
                    if (currFavs.length) {
                        allItems.push({
                            type: currFavs[0].type!,
                            itemIds: currFavs.map((f) => f.id),
                        });
                    }
                }

                currentAll = {
                    allItemIds: allItems,
                    id: '-1',
                    title: this.siteCore.favouritesDashboard.AllPill,
                    icon: currBase[0].type === FavouriteType.League ? 'country-icon c6' : '',
                    count: currSportCount,
                    type: groups[group][0].type,
                    active: false,
                    classes: this.getClasses(currSportCount),
                };

                currBase.unshift(currentAll);
            }

            const userState = this.stateService.get(routeTab, groups[group][0].sport.id.toString());
            const selectedItem = currBase.find((c) => c.id === userState && !!c.count);

            if (userState && selectedItem) {
                selectedItem.active = true;
            } else {
                currBase[0].active = true;
            }

            const sport = groups[group][0].sport;
            value.push({
                gridHeaderPills: currBase,
                id: sport.id.toString(),
                title: this.translatorService.translate(routeTab, sport.id, sport.name),
                icon: 'sports-icon sports-' + sport.id.toString(),
                count: currSportCount,
                type: favs[0].type,
                active: false,
                expanded: true,
                classes: this.getClasses(currSportCount),
            });
        }

        return value;
    }

    getGeneralPills(
        routeTab: keyof FavouritesSectionTabState,
        favs: FavouritesViewModel[],
        hasAdditionalFilters: boolean,
        isUpcomming: boolean = false,
    ): FavouritesSectionModel[] {
        if (!favs?.length) {
            return [];
        }

        const sports = this.getUniqueSports(favs, isUpcomming);

        const value: FavouritesSectionModel[] = [];
        let filteredBySport: FavouritesViewModel[] = [];

        for (const sport of sports) {
            filteredBySport = favs.filter((f) => f.sport.id === sport.id);
            const count = sumBy(filteredBySport, (filtered) => filtered.count);
            const item: FavouritesSectionModel = {
                id: sport.id.toString(),
                title: hasAdditionalFilters ? sport.name : this.translatorService.translate(routeTab, sport.id, sport.name),
                icon: 'sports-icon sports-' + sport.id.toString(),
                count,
                active: false,
                expanded: !hasAdditionalFilters,
                classes: this.getClasses(count),
            };
            if (hasAdditionalFilters) {
                item.additionalFilters = this.getAdditionalFiltersForGeneralPills(filteredBySport);
            }
            value.push(item);
        }

        if (hasAdditionalFilters && sports.length > 1) {
            const count = value.reduce((cnt, o) => {
                return cnt + o.count!;
            }, 0);
            value.unshift({
                gridHeaderPills: [],
                additionalFilters: this.getAdditionalFiltersForGeneralPills(favs),
                id: '-1',
                title: this.siteCore.favouritesDashboard.AllPill,
                icon: 'sports-icon sports-all',
                count,
                active: false,
                classes: this.getClasses(count),
            });
        }
        if (hasAdditionalFilters) {
            const userState = this.stateService.get(routeTab);
            const selectedItem = value.find((v) => v.id === userState && !!v.count);

            if (userState && selectedItem) {
                selectedItem.active = true;
            } else {
                value[0].active = true;
            }
        }

        return value;
    }

    private getAdditionalFiltersForGeneralPills(favs: FavouritesViewModel[]): StringDictionary<FavouritedItems[]> {
        if (!favs?.length) {
            return {};
        }

        const filters: StringDictionary<FavouritedItems[]> = {};

        const favouritesBySport = groupBy(favs, (fav) => fav.sport.id);
        forIn(favouritesBySport, (sportFavs, sportId) => {
            const sportFilter: FavouritedItems[] = [];
            for (const item in FavouriteType) {
                const currFavs = sportFavs.filter((f) => f.type === FavouriteType[item as keyof typeof FavouriteType]);
                if (currFavs.length) {
                    sportFilter.push({
                        type: currFavs[0].type,
                        itemIds: currFavs.map((f) => f.itemId),
                    });
                }
            }
            filters[sportId.toString()] = sportFilter;
        });

        return filters;
    }

    private getUniqueSports(favs: FavouritesViewModel[], isUpcomming: boolean): FavouritesSport[] {
        let value: FavouritesViewModel[] = uniqBy(favs, (fav) => fav.sport.id);
        if (isUpcomming) {
            value = sortBy(value, (el) => el.upcomingOrder);
        } else {
            value = sortBy(value, (el) => el.order);
        }

        return value.map((f) => f.sport);
    }

    private getFavouritesTabFilter(filter?: FavouritesTabFilter): FavouritesTabFilter {
        return filter || { generalPills: [], gridSections: [] };
    }

    private getClasses(count: number): string | undefined {
        return count ? undefined : 'disabled';
    }
}
